// src/PmSchedule.js
import { useEffect, useMemo, useState } from "react";
import api from "./api";
import { Pencil, Trash2, Save, X, Archive } from "lucide-react";

const emptyForm = {
  job_id: "",
  date: "",
  line: "",
  machine: "",
  tasks: "",
  frequency: "",
  frequency_unit: "months",
  assignee: "",
  is_active: true,
};

function toDateSafe(v) {
  if (!v) return null;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
}

function isSameMonthYear(d, now) {
  return (
    d &&
    now &&
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth()
  );
}

function daysUntil(d, now) {
  if (!d || !now) return Number.POSITIVE_INFINITY;
  const ms = d.getTime() - now.getTime();
  return Math.floor(ms / (1000 * 60 * 60 * 24));
}

export default function PmSchedule() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showInactive, setShowInactive] = useState(false);

  const [filterLine, setFilterLine] = useState("");
  const [filterMachine, setFilterMachine] = useState("");
  const [filterDate, setFilterDate] = useState("");
  const [filterFrequencyUnit, setFilterFrequencyUnit] = useState("");

  async function loadPmSchedules() {
    try {
      setLoading(true);
      setError("");

      const res = await api.get("/api/pm-schedules");
      const data = res.data;
      const list = Array.isArray(data)
        ? data
        : data?.pm_schedules || data?.items || [];

      setItems(list);
    } catch (err) {
      console.error(err);
      setError("Could not load PM schedules.");
      setItems([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadPmSchedules();
  }, []);

  function handleChange(e) {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");

    if (
      !form.job_id ||
      !form.date ||
      !form.line ||
      !form.machine ||
      !form.tasks ||
      !form.frequency
    ) {
      setError("Please fill Job ID, Date, Line, Machine, Tasks, and Frequency.");
      return;
    }

    const payload = {
      job_id: form.job_id.trim(),
      date: form.date,
      line: form.line.trim(),
      machine: form.machine.trim(),
      tasks: form.tasks,
      frequency: Number(form.frequency),
      frequency_unit: form.frequency_unit || "months",
      assignee: (form.assignee || "").trim() || null,
      is_active: !!form.is_active,
    };

    try {
      if (editingId) {
        await api.put(`/api/pm-schedules/${editingId}`, payload);
      } else {
        await api.post("/api/pm-schedules", payload);
      }

      await loadPmSchedules();
      setForm(emptyForm);
      setEditingId(null);
    } catch (err) {
      console.error(err);
      const msg =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err.message ||
        "Failed to save PM schedule";
      setError(msg);
    }
  }

  function handleEdit(item) {
    setEditingId(item.id);
    setForm({
      job_id: item.job_id || "",
      date: item.date || "",
      line: item.line || "",
      machine: item.machine || "",
      tasks: item.tasks || "",
      frequency: String(item.frequency ?? ""),
      frequency_unit: item.frequency_unit || "months",
      assignee: item.assignee || "",
      is_active: item.is_active !== false,
    });
  }

  async function handleDelete(id) {
    if (!window.confirm("Delete this PM schedule?")) return;

    try {
      await api.delete(`/api/pm-schedules/${id}`);
      setItems((prev) => prev.filter((x) => x.id !== id));

      if (editingId === id) {
        setEditingId(null);
        setForm(emptyForm);
      }
    } catch (err) {
      console.error(err);
      setError("Could not delete PM schedule.");
    }
  }

  async function handleClose(id) {
    if (!window.confirm("Close this PM schedule and create the next one?")) return;

    try {
      await api.post(`/api/pm-schedules/${id}/close`);
      await loadPmSchedules();
    } catch (err) {
      console.error(err);
      const msg =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err.message ||
        "Failed to close PM schedule";
      setError(msg);
    }
  }

  function handleCancelEdit() {
    setEditingId(null);
    setForm(emptyForm);
  }

  function clearFilters() {
    setFilterLine("");
    setFilterMachine("");
    setFilterDate("");
    setFilterFrequencyUnit("");
  }

  const sortedItems = useMemo(() => {
    const now = new Date();

    let filteredItems = showInactive
      ? items
      : items.filter((pm) => pm.is_active !== false);

    if (filterLine.trim()) {
      filteredItems = filteredItems.filter((pm) =>
        (pm.line || "").toLowerCase().includes(filterLine.toLowerCase())
      );
    }

    if (filterMachine.trim()) {
      filteredItems = filteredItems.filter((pm) =>
        (pm.machine || "").toLowerCase().includes(filterMachine.toLowerCase())
      );
    }

    if (filterDate) {
      filteredItems = filteredItems.filter((pm) => pm.date === filterDate);
    }

    if (filterFrequencyUnit) {
      filteredItems = filteredItems.filter(
        (pm) => (pm.frequency_unit || "months") === filterFrequencyUnit
      );
    }

    const decorated = (filteredItems || []).map((pm) => {
      const d = toDateSafe(pm.date);
      const isActive = pm.is_active !== false;
      const dueThisMonth = isActive && isSameMonthYear(d, now);
      const delta = daysUntil(d, now);

      const isOverdue = isActive && delta < 0;
      const isDueSoon = isActive && delta >= 0 && delta <= 3;

      return { pm, d, isActive, dueThisMonth, delta, isOverdue, isDueSoon };
    });

    decorated.sort((a, b) => {
      if (a.isActive !== b.isActive) return a.isActive ? -1 : 1;

      if (a.isOverdue !== b.isOverdue) return a.isOverdue ? -1 : 1;

      if (a.isDueSoon !== b.isDueSoon) return a.isDueSoon ? -1 : 1;

      if (a.dueThisMonth !== b.dueThisMonth) {
        return a.dueThisMonth ? -1 : 1;
      }

      const aTime = a.d ? a.d.getTime() : Number.POSITIVE_INFINITY;
      const bTime = b.d ? b.d.getTime() : Number.POSITIVE_INFINITY;

      if (aTime !== bTime) return aTime - bTime;

      return (a.pm?.id ?? 0) - (b.pm?.id ?? 0);
    });

    return decorated;
  }, [items, showInactive, filterLine, filterMachine, filterDate, filterFrequencyUnit]);

  return (
    <div className="main-content">
      <style>{`
        @keyframes pmFlicker {
          0%, 100% { opacity: 1; transform: scale(1); filter: drop-shadow(0 0 0 rgba(239,68,68,0)); }
          50% { opacity: 0.35; transform: scale(1.05); filter: drop-shadow(0 0 10px rgba(239,68,68,0.55)); }
        }

        .pm-active-indicator {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          font-weight: 600;
          font-size: 12px;
          padding: 6px 10px;
          border-radius: 999px;
          border: 1px solid rgba(148,163,184,0.25);
          background: rgba(255,255,255,0.03);
          white-space: nowrap;
        }

        .pm-indicator-dot {
          width: 10px;
          height: 10px;
          border-radius: 999px;
          display: inline-block;
        }

        .pm-dot-green {
          background: rgba(16,185,129,1);
          box-shadow: 0 0 0 4px rgba(16,185,129,0.15);
        }

        .pm-dot-gray {
          background: rgba(148,163,184,1);
          box-shadow: 0 0 0 4px rgba(148,163,184,0.12);
        }

        .pm-dot-red {
          background: rgba(239,68,68,1);
          box-shadow: 0 0 0 4px rgba(239,68,68,0.15);
          animation: pmFlicker 1s infinite;
        }

        .pm-dot-orange {
          background: rgba(249,115,22,1);
          box-shadow: 0 0 0 4px rgba(249,115,22,0.15);
        }

        .pm-row-due {
          background: rgba(239,68,68,0.06);
        }

        .pm-row-overdue {
          background: rgba(220,38,38,0.12);
        }

        .pm-row-soon {
          background: rgba(249,115,22,0.10);
        }

        .pm-row-due td,
        .pm-row-overdue td,
        .pm-row-soon td {
          border-bottom-color: rgba(239,68,68,0.18);
        }

        .pm-date-badge {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 6px 10px;
          border-radius: 10px;
          border: 1px solid rgba(148,163,184,0.25);
          background: rgba(255,255,255,0.03);
          font-weight: 600;
          font-size: 12px;
          white-space: nowrap;
        }

        .pm-date-badge.red {
          border-color: rgba(239,68,68,0.35);
          background: rgba(239,68,68,0.08);
        }

        .pm-date-badge.orange {
          border-color: rgba(249,115,22,0.35);
          background: rgba(249,115,22,0.08);
        }
      `}</style>

      <div className="toolbar">
        <div className="toolbar-left">
          <div className="toolbar-title">PM Schedule</div>
          <div className="toolbar-subtitle">
            Manage preventive maintenance jobs by days, weeks, or months.
          </div>
        </div>
      </div>

      <div className="card">
        <h2 style={{ marginTop: 0, marginBottom: 12 }}>
          {editingId ? "Edit PM Job" : "New PM Job"}
        </h2>

        {error && (
          <div className="badge badge-danger" style={{ marginBottom: 10 }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 12 }}>
            <div>
              <label>Job ID</label>
              <input name="job_id" value={form.job_id} onChange={handleChange} placeholder="PM-L1-FILLER-001" />
            </div>

            <div>
              <label>Next Date</label>
              <input type="date" name="date" value={form.date} onChange={handleChange} />
            </div>

            <div>
              <label>Line</label>
              <input name="line" value={form.line} onChange={handleChange} placeholder="Line A" />
            </div>

            <div>
              <label>Machine</label>
              <input name="machine" value={form.machine} onChange={handleChange} placeholder="Filler, Packer..." />
            </div>

            <div>
              <label>Frequency</label>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                <input type="number" name="frequency" value={form.frequency} onChange={handleChange} placeholder="2" min="1" />

                <select name="frequency_unit" value={form.frequency_unit} onChange={handleChange}>
                  <option value="days">Days</option>
                  <option value="weeks">Weeks</option>
                  <option value="months">Months</option>
                </select>
              </div>
            </div>

            <div>
              <label>Assignee</label>
              <input name="assignee" value={form.assignee} onChange={handleChange} placeholder="Technician name" />
            </div>
          </div>

          <div style={{ marginTop: 10 }}>
            <label>Tasks</label>
            <textarea name="tasks" value={form.tasks} onChange={handleChange} placeholder="Describe the PM steps to perform..." />
          </div>

          <div style={{ display: "flex", gap: 8, marginTop: 10, alignItems: "center" }}>
            <button type="submit" className="add-button">
              <Save size={16} />
              {editingId ? "Save Changes" : "Add PM Job"}
            </button>

            {editingId && (
              <button type="button" className="button-secondary" onClick={handleCancelEdit}>
                <X size={16} />
                Cancel Edit
              </button>
            )}

            <label style={{ marginLeft: "auto", display: "flex", gap: 8, alignItems: "center" }}>
              <input type="checkbox" name="is_active" checked={!!form.is_active} onChange={handleChange} />
              Active
            </label>
          </div>
        </form>
      </div>

      <div className="card">
        <div style={{ display: "flex", gap: 12, alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <h2 style={{ margin: 0 }}>All PM Jobs</h2>

            <button type="button" className="button-secondary" onClick={() => setShowInactive((prev) => !prev)}>
              {showInactive ? "Hide Inactive" : "Show Inactive"}
            </button>
          </div>

          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <span className="pm-active-indicator"><span className="pm-indicator-dot pm-dot-green" />Active</span>
            <span className="pm-active-indicator"><span className="pm-indicator-dot pm-dot-red" />Overdue / Due this month</span>
            <span className="pm-active-indicator"><span className="pm-indicator-dot pm-dot-orange" />Due soon</span>
            <span className="pm-active-indicator"><span className="pm-indicator-dot pm-dot-gray" />Inactive</span>
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(5, minmax(0, 1fr))", gap: 10, marginBottom: 14 }}>
          <input placeholder="Filter by line..." value={filterLine} onChange={(e) => setFilterLine(e.target.value)} />
          <input placeholder="Filter by machine..." value={filterMachine} onChange={(e) => setFilterMachine(e.target.value)} />
          <input type="date" value={filterDate} onChange={(e) => setFilterDate(e.target.value)} />

          <select value={filterFrequencyUnit} onChange={(e) => setFilterFrequencyUnit(e.target.value)}>
            <option value="">All frequencies</option>
            <option value="days">Days</option>
            <option value="weeks">Weeks</option>
            <option value="months">Months</option>
          </select>

          <button type="button" className="button-secondary" onClick={clearFilters}>
            Clear Filters
          </button>
        </div>

        {loading ? (
          <p className="text-muted">Loading…</p>
        ) : items.length === 0 ? (
          <p className="text-muted">No PM schedules yet.</p>
        ) : sortedItems.length === 0 ? (
          <p className="text-muted">No PM jobs match your filters.</p>
        ) : (
          <div className="table-wrapper">
            <div className="table-scroll">
              <table>
                <thead>
                  <tr>
                    <th>Job ID</th>
                    <th>Date</th>
                    <th>Line</th>
                    <th>Machine</th>
                    <th>Tasks</th>
                    <th>Frequency</th>
                    <th>Assignee</th>
                    <th>Status</th>
                    <th style={{ width: 140 }}>Actions</th>
                  </tr>
                </thead>

                <tbody>
                  {sortedItems.map(({ pm, dueThisMonth, isActive, isOverdue, isDueSoon }) => {
                    const dotClass = !isActive
                      ? "pm-dot-gray"
                      : isOverdue
                      ? "pm-dot-red"
                      : isDueSoon
                      ? "pm-dot-orange"
                      : dueThisMonth
                      ? "pm-dot-red"
                      : "pm-dot-green";

                    const activeLabel = !isActive
                      ? "Inactive"
                      : isOverdue
                      ? "Overdue"
                      : isDueSoon
                      ? "Due soon"
                      : dueThisMonth
                      ? "Due this month"
                      : "Active";

                    const dateBadgeClass = isOverdue || dueThisMonth ? "red" : isDueSoon ? "orange" : "";

                    return (
                      <tr
                        key={pm.id}
                        className={
                          isOverdue
                            ? "pm-row-overdue"
                            : isDueSoon
                            ? "pm-row-soon"
                            : dueThisMonth
                            ? "pm-row-due"
                            : ""
                        }
                      >
                        <td style={{ fontWeight: 700 }}>{pm.job_id}</td>
                        <td>
                          <span className={`pm-date-badge ${dateBadgeClass}`}>
                            {pm.date}
                          </span>
                        </td>
                        <td>{pm.line}</td>
                        <td>{pm.machine}</td>
                        <td style={{ maxWidth: 260, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{pm.tasks}</td>
                        <td>Every {pm.frequency} {pm.frequency_unit || "months"}</td>
                        <td>{pm.assignee || "—"}</td>
                        <td>
                          <span className="pm-active-indicator">
                            <span className={`pm-indicator-dot ${dotClass}`} />
                            {activeLabel}
                          </span>
                        </td>
                        <td>
                          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                            <button type="button" className="icon-btn" onClick={() => handleEdit(pm)} title="Edit">
                              <Pencil size={16} />
                            </button>

                            <button type="button" className="icon-btn danger" onClick={() => handleDelete(pm.id)} title="Delete">
                              <Trash2 size={16} />
                            </button>

                            {isActive && (
                              <button type="button" className="icon-btn ghost" onClick={() => handleClose(pm.id)} title="Close">
                                <Archive size={16} />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}